import { CommonModule } from '@angular/common';
import { Component, Inject, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { ControleEstoqueService } from '../../../../../shared/services/apis/produto/controle-estoque.service';
import { AtualizarDatasLoteRequest } from '../../../../../shared/services/requests/produto/atualizar-datas-lote.request';
import { AlertService, AlertType } from '../../../../../shared/services/alert.service';
import { MaskService } from '../../../../../shared/services/mask.service';

export interface AlterarDataLoteDialogData {
  idLote: number;
  numeroLote: string;
  dataFabricacao: string | null; // ISO date
  dataValidade: string | null; // ISO date
}

@Component({
  selector: 'app-alterar-data-lote-dialog',
  standalone: true,
  imports: [
    CommonModule,
    MatDialogModule,
    MatButtonModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatDatepickerModule,
    MatProgressSpinnerModule,
    FormsModule,
  ],
  templateUrl: './alterar-data-lote-dialog.component.html',
  styleUrls: ['./alterar-data-lote-dialog.component.css'],
})
export class AlterarDataLoteDialogComponent implements OnInit {
  public novaDataFabricacao: Date | null = null;
  public novaDataValidade: Date | null = null;
  public loading: boolean = false;
  public readonly hoje: Date = new Date();

  constructor(
    public dialogRef: MatDialogRef<AlterarDataLoteDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: AlterarDataLoteDialogData,
    private controleEstoqueService: ControleEstoqueService,
    private alertService: AlertService
  ) {}

  ngOnInit(): void {
    if (this.data.dataFabricacao) {
      this.novaDataFabricacao = this.fromDateOnly(this.data.dataFabricacao);
    }
    if (this.data.dataValidade) {
      this.novaDataValidade = new Date(this.data.dataValidade);
    }
  }

  /** A fabricação é um dia ("yyyy-MM-dd"): lê como data local, porque `new Date('2026-10-23')` é UTC e recuaria um dia. */
  private fromDateOnly(valor: string): Date {
    const [ano, mes, dia] = valor.substring(0, 10).split('-').map(Number);
    return new Date(ano, mes - 1, dia);
  }

  public get podeSalvar(): boolean {
    return !this.loading && (!!this.novaDataFabricacao || !!this.novaDataValidade);
  }

  public salvar(): void {
    if (!this.podeSalvar) return;

    if (
      this.novaDataFabricacao &&
      this.novaDataValidade &&
      this.novaDataValidade < this.novaDataFabricacao
    ) {
      this.alertService.show(
        'Atenção',
        'A data de validade não pode ser menor que a data de fabricação.',
        AlertType.warning
      );
      return;
    }

    this.loading = true;
    const request: AtualizarDatasLoteRequest = {
      dataFabricacao: this.novaDataFabricacao ? this.toDateOnly(this.novaDataFabricacao) : null,
      dataValidade: this.novaDataValidade ? this.toDateOnly(this.novaDataValidade) : null,
    };

    this.controleEstoqueService.atualizarDatasLote(this.data.idLote, request).subscribe(
      (response) => {
        this.loading = false;
        if (!response.isError) {
          this.alertService.show('Sucesso', 'Datas atualizadas com sucesso.', AlertType.success);
          this.dialogRef.close(true);
        } else {
          this.alertService.show('Aviso', response.errorDescription || 'Erro ao atualizar.', AlertType.warning);
        }
      },
      () => {
        this.loading = false;
        this.alertService.show('Erro', 'Ocorreu um erro ao atualizar as datas.', AlertType.error);
      }
    );
  }

  public fechar(): void {
    this.dialogRef.close(false);
  }

  public onDateInput(event: Event): void {
    MaskService.onDateInput(event);
  }

  public onDateKeydown(event: KeyboardEvent): void {
    const teclasPermitidas = ['Backspace', 'Delete', 'Tab', 'ArrowLeft', 'ArrowRight', 'Home', 'End', 'Enter'];
    const isAtalho = (event.ctrlKey || event.metaKey) && ['a', 'c', 'v', 'x'].includes(event.key.toLowerCase());
    if (teclasPermitidas.includes(event.key) || isAtalho) return;
    if (!/^\d$/.test(event.key)) event.preventDefault();
  }

  public onDataFabricacaoBlur(event: FocusEvent): void {
    const input = event.target as HTMLInputElement;
    const data = this.lerDataInput(input);
    if (data && data > this.hojeNormalizado()) {
      input.value = '';
      this.novaDataFabricacao = null;
      this.alertService.show('Atenção', 'A data de fabricação não pode ser maior que a data atual.', AlertType.warning);
      return;
    }
    if (data && this.novaDataValidade && data > this.novaDataValidade) {
      input.value = '';
      this.novaDataFabricacao = null;
      this.alertService.show('Atenção', 'A data de fabricação não pode ser maior que a data de validade.', AlertType.warning);
      return;
    }
    this.novaDataFabricacao = data;
  }

  public onDataValidadeBlur(event: FocusEvent): void {
    const input = event.target as HTMLInputElement;
    const data = this.lerDataInput(input);
    if (data && this.novaDataFabricacao && data < this.novaDataFabricacao) {
      input.value = '';
      this.novaDataValidade = null;
      this.alertService.show('Atenção', 'A data de validade não pode ser menor que a data de fabricação.', AlertType.warning);
      return;
    }
    this.novaDataValidade = data;
  }

  private lerDataInput(input: HTMLInputElement): Date | null {
    const valor = (input?.value || '').trim();
    if (!valor) return null;
    const apenasNumeros = valor.replace(/\D/g, '').substring(0, 8);
    if (apenasNumeros.length !== 8) {
      input.value = '';
      this.alertService.show('Atenção', 'Informe uma data válida no formato DD/MM/AAAA.', AlertType.warning);
      return null;
    }
    const formatado = `${apenasNumeros.substring(0, 2)}/${apenasNumeros.substring(2, 4)}/${apenasNumeros.substring(4, 8)}`;
    input.value = formatado;
    const data = this.parseDataBr(formatado);
    if (!data) {
      input.value = '';
      this.alertService.show('Atenção', 'Informe uma data válida no formato DD/MM/AAAA.', AlertType.warning);
    }
    return data;
  }

  private parseDataBr(valor: string): Date | null {
    const partes = valor.split('/');
    if (partes.length !== 3) return null;
    const dia = Number(partes[0]);
    const mes = Number(partes[1]);
    const ano = Number(partes[2]);
    if (!Number.isInteger(dia) || !Number.isInteger(mes) || !Number.isInteger(ano)) return null;
    if (ano < 1000 || ano > 9999 || mes < 1 || mes > 12 || dia < 1 || dia > 31) return null;
    const data = new Date(ano, mes - 1, dia);
    data.setHours(0, 0, 0, 0);
    if (data.getFullYear() !== ano || data.getMonth() !== mes - 1 || data.getDate() !== dia) return null;
    return data;
  }

  private hojeNormalizado(): Date {
    const h = new Date();
    h.setHours(0, 0, 0, 0);
    return h;
  }

  private toDateOnly(d: Date): string {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  }
}
